import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { Link } from "react-router-dom";

import Layout from "src/components/layout/dashboard/Layout";
import StockMovementCard from "src/components/dashboard/StockMovementCard";
import SalesOverviewCard from "src/components/dashboard/SalesOverviewCard";
import PurchaseOverviewCard from "src/components/dashboard/PurchaseOverviewCard";
import CategorySalesCard from "src/components/dashboard/CategorySalesCard";
import TopProductsCard from "src/components/dashboard/TopProductsCard";
import RecentSalesCard from "src/components/dashboard/RecentSalesCard";
import RecentPurchaseCard from "src/components/dashboard/RecentPurchaseCard";
import LowStockCard from "src/components/dashboard/LowStockCard";
import * as dashboardActions from "src/redux/slices/dashboardSlice";
import * as warehouseActions from "src/redux/slices/warehouseSlice";
import { currencyFormatter } from "src/utils/common";
import Popover from "src/components/ui/Popover";
import { Info } from "lucide-react";
import usePlanUsage from "src/hooks/usePlanUsage";
import useAssignedWarehouse from "src/hooks/useAssignedWarehouse";
import useWarehouseLabel from "src/hooks/useWarehouseLabel";
import FilterSelect from "src/components/ui/FilterSelect";
import Skeleton from "src/components/ui/Skeleton";

const DashboardPage: React.FC = () => {
  // const {
  //   user: { data: user },
  // } = useAppSelector((state) => state.authTenant);
  // if (user?.userType === "IN_HOUSE" && user?.role === "admin")
  //   return <SuperAdminDashboard />;
  // if (user?.role === "admin") return <TenantAdminDashboard />;
  // else if (user?.role === "manager") return <ManagerDashboard />;
  // else if (user?.role === "staff") return <StaffDashboard />;
  // return null;

  const {
    user: { data: user },
  } = useAppSelector((state) => state.authTenant);

  const permissions = user?.roleDetails?.permissions || user?.permissions || {};
  const isTenantAdmin =
    user?.role === "admin" ||
    user?.roleDetails?.name?.toLowerCase() === "admin";
  const permissionEntries = Object.keys(permissions || {});
  const shouldFilter = permissionEntries.length > 0 && !isTenantAdmin;
  const canSeeModule = (module: string) => {
    if (!shouldFilter) return true;
    return (permissions?.[module] || []).length > 0;
  };

  const dispatch = useAppDispatch();
  const { planUsage, loading: planUsageLoading } = usePlanUsage();

  const { tenantDashboardSummary } = useAppSelector((state) => state.dashboard);
  const { warehouseList } = useAppSelector((state) => state.warehouse);
  const { assignedWarehouseId, isWarehouseLocked } = useAssignedWarehouse();
  const { singular: warehouseLabel, plural: warehouseLabelPlural } = useWarehouseLabel();
  const [warehouseId, setWarehouseId] = useState("");

  const dashboardPeriodDays = 7;
  const dashboardPeriodLabel = `${dashboardPeriodDays} days`;

  useEffect(() => {
    dispatch(warehouseActions.getWarehouseListRequest({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (isWarehouseLocked && assignedWarehouseId) setWarehouseId(assignedWarehouseId);
  }, [isWarehouseLocked, assignedWarehouseId]);

  const warehouseOptions = (warehouseList?.data?.data || []).map((w: any) => ({
    label: w.name,
    value: w._id,
  }));

  const showProducts = canSeeModule("products");
  // "inventory" used to be one bundled permission covering warehouses,
  // stock levels, stock adjustment, stock movements and transfers — now
  // that those are granted separately, show these inventory-wide widgets
  // if the role has access to any of them.
  const showInventory =
    canSeeModule("warehouses") ||
    canSeeModule("stockLevels") ||
    canSeeModule("stockAdjustment") ||
    canSeeModule("stockMovements") ||
    canSeeModule("transfers");
  const showSales = canSeeModule("sales");
  const showPurchases = canSeeModule("purchases");
  const showStockMovement = showInventory || showProducts;
  const showLowStock = showInventory || showProducts;
  const needsSummary =
    showProducts || showInventory || showSales || showPurchases;
  const canReorder =
    isTenantAdmin || (permissions?.purchases || []).includes("create");

  useEffect(() => {
    if (needsSummary) {
      dispatch(
        dashboardActions.getTenantDashboardSummaryRequest({
          warehouseId: warehouseId || undefined,
        })
      );
    }
  }, [
    dispatch,
    needsSummary,
    warehouseId,
  ]);

  const summary = tenantDashboardSummary.data;
  const usage = planUsage?.usage;
  const kpiLoading = (needsSummary && tenantDashboardSummary.loading) || planUsageLoading;
  const kpiCards = [
    showProducts && {
      label: "Total Products",
      value: usage?.totalProducts ?? summary?.totalProducts ?? 0,
      description: "Active products in your catalog.",
      actionLabel: "View products",
      actionPath: "/app/product/list",
    },
    showInventory && {
      label: "Stock Value",
      value: currencyFormatter(summary?.stockValue ?? 0),
      description:
        "Total inventory value based on current stock and pricing (sum of available stock × selling price).",
      actionLabel: "View stock levels",
      actionPath: "/app/stock/levels",
    },
    showInventory && {
      label: "Low Stock Alerts",
      value: summary?.lowStockCount ?? 0,
      description: "Products currently below their configured minimum stock threshold.",
      actionLabel: "View stock levels",
      actionPath: "/app/stock/levels",
    },
    showInventory && {
      label: "Adjustments (In/Out)",
      value: `${summary?.adjustmentIn ?? 0}/${summary?.adjustmentOut ?? 0}`,
      description:
        "Completed stock adjustments by quantity (format: in/out).",
      actionLabel: "View stock levels",
      actionPath: "/app/stock/levels",
    },
    showSales && {
      label: "Sales Orders",
      value: usage?.totalSaleOrders ?? summary?.totalSalesOrders ?? 0,
      description: "Completed sales orders.",
      actionLabel: "View sales",
      actionPath: "/app/sale/list",
    },
    showSales && {
      label: "Pending Sales",
      value: summary?.pendingSalesOrders ?? 0,
      description: "Sales orders that are not yet completed or closed.",
      actionLabel: "View pending sales",
      actionPath: "/app/sale/list",
    },
    showPurchases && {
      label: "Purchase Orders",
      value: usage?.totalPurchaseOrders ?? summary?.totalPurchaseOrders ?? 0,
      description: "Completed purchase orders.",
      actionLabel: "View purchases",
      actionPath: "/app/purchase/list",
    },
    showPurchases && {
      label: "Pending Purchases",
      value: summary?.pendingPurchaseOrders ?? 0,
      description: "Purchase orders that are not yet completed or closed.",
      actionLabel: "View pending purchases",
      actionPath: "/app/purchase/list",
    },
  ].filter(Boolean) as {
    label: string;
    value: string | number;
    description?: string;
    actionLabel?: string;
    actionPath?: string;
  }[];

  return (
    <Layout
    // meta={{
    //   title: "Dashboard | Invyntra",
    //   description: "",
    //   keywords: "sales, products, invoices, inventory, invyntra",
    // }}
    >
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Welcome {user?.firstName}!
            </h1>
            {user?.tenantDetails?.name && (
              <p className="text-sm text-gray-500 mt-0.5 dark:text-gray-400">
                {user.tenantDetails.name}
              </p>
            )}
          </div>
          {warehouseOptions.length > 0 && (
            <FilterSelect
              variant="pill"
              label={`All ${warehouseLabelPlural.toLowerCase()}`}
              value={warehouseId}
              onChange={setWarehouseId}
              allLabel={`All ${warehouseLabelPlural.toLowerCase()}`}
              options={warehouseOptions}
              disabled={isWarehouseLocked}
            />
          )}
        </div>

        {/* Dashboard Content */}
        <div className="space-y-6">
          {kpiCards.length > 0 && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {kpiCards.map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800"
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">{card.label}</p>
                    {card.description && (
                      <Popover
                        openOnHover
                        trigger={
                          <button className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                            <Info size={14} />
                          </button>
                        }
                      >
                        <div className="max-w-xs px-3 py-2 text-xs text-gray-600 dark:text-gray-300">
                          {card.description}
                        </div>
                      </Popover>
                    )}
                  </div>
                  {kpiLoading ? (
                    <Skeleton className="mt-2 h-8 w-20" />
                  ) : (
                    <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
                      {card.value}
                    </p>
                  )}
                  <div className="mt-4 flex items-center justify-between">
                    <div className="h-1 w-12 rounded-full bg-primary-200" />
                    {card.actionPath && card.actionLabel && (
                      <Link
                        to={card.actionPath}
                        className="text-xs text-primary-600 hover:text-primary-700"
                      >
                        {card.actionLabel}
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {(showStockMovement || showSales || showPurchases) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <StockMovementCard
                days={dashboardPeriodDays}
                periodLabel={dashboardPeriodLabel}
                enabled={showStockMovement}
                warehouseId={warehouseId}
              />
              <SalesOverviewCard
                days={dashboardPeriodDays}
                periodLabel={dashboardPeriodLabel}
                enabled={showSales}
                warehouseId={warehouseId}
              />
              <PurchaseOverviewCard
                days={dashboardPeriodDays}
                periodLabel={dashboardPeriodLabel}
                enabled={showPurchases}
                warehouseId={warehouseId}
              />
            </div>
          )}

          <LowStockCard
            enabled={showLowStock}
            canReorder={canReorder}
            limit={5}
            warehouseId={warehouseId}
          />

          {(showSales || showProducts) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <CategorySalesCard
                days={dashboardPeriodDays}
                periodLabel={dashboardPeriodLabel}
                limit={6}
                enabled={showSales}
                warehouseId={warehouseId}
              />
              <TopProductsCard
                days={dashboardPeriodDays}
                periodLabel={dashboardPeriodLabel}
                limit={6}
                enabled={showSales}
                warehouseId={warehouseId}
              />
            </div>
          )}

          {(showSales || showPurchases) && (
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <RecentSalesCard limit={5} enabled={showSales} warehouseId={warehouseId} />
              <RecentPurchaseCard limit={5} enabled={showPurchases} warehouseId={warehouseId} />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default DashboardPage;
